import { TransactionDetailsTab } from 'app/AgentDetails/config/AgentTransactionTabConfig';
import { WorkspaceTabKeys } from 'app/AgentDetails/WorkspaceTab/WorkspaceTab';
import type { RulesType } from 'app/services/documentRules.types';
import type { ConfigKind as FactConfigKind } from 'app/services/factConfig.types';

const prefix = '';

const buildTaskFormUrl = (
  prefix: string,
  basePath: string,
  templateId: string,
  taskId: string,
  isDuplicate?: boolean
): string => {
  const url = `${prefix}${basePath}?templateId=${templateId}&taskId=${taskId}`;
  return isDuplicate === true ? `${url}&isDuplicate=true` : url;
};

export const AppURLs = {
  // Landing Page
  landingPage: () => '/',

  // Help / support requests. Mounted per dashboard shell so the sidebar stays.
  agentDashboardHelp: () => prefix + '/agent/dashboard/help',
  complianceDashboardHelp: () => prefix + '/compliance/dashboard/help',
  officeAdminHelp: () => prefix + '/office-admin/dashboard/help',

  agentDashboardFormsLibrary: () => prefix + '/agent/dashboard/forms-library',
  complianceDashboardFormsLibrary: () =>
    prefix + '/compliance/dashboard/forms-library',

  // Auth
  agentLogin: () => prefix + '/agent/login',
  agentLoginSessionExpired: () => prefix + '/agent/login?sessionExpired=true',

  // Agent Onboarding
  agentOnboardingAddEmail: (partnerID: string = '') =>
    prefix + `/agent/onboarding/add-email?partnerID=${partnerID}`,
  // Agent Dashboard Main
  agentDashboardMainHome: () => prefix + '/agent/dashboard/main/home',
  agentDashboardMainTransactions: () =>
    prefix + '/agent/dashboard/main/transactions',
  agentDashboardMainTasks: () => prefix + '/agent/dashboard/main/tasks',
  agentDashboardMainReporting: () => prefix + '/agent/dashboard/main/reporting',
  agentDashboardMainTaskTemplates: () =>
    prefix + '/agent/dashboard/task-templates',
  agentDashboardMainTaskForm: (templateId: string) =>
    prefix +
    `/agent/dashboard/task-templates/task-form?templateId=${templateId}`,
  agentDashboardMainTaskFormWithTaskId: (
    templateId: string,
    taskId: string,
    isDuplicate?: boolean
  ) =>
    buildTaskFormUrl(
      prefix,
      '/agent/dashboard/task-templates/task-form',
      templateId,
      taskId,
      isDuplicate
    ),
  agentDashboardMainContacts: () => prefix + '/agent/dashboard/main/contacts',
  agentDashboardMainClients: () => prefix + '/agent/dashboard/main/clients',
  agentDashboardBuyerOffer: () => prefix + '/agent/dashboard/main/buyer-offer',
  agentDashboardLeaseOffer: () => prefix + '/agent/dashboard/main/lease-offer',
  agentDashboardListingOffer: () =>
    prefix + '/agent/dashboard/main/listing-offer',
  agentDashboardLeaseListingOffer: () =>
    prefix + '/agent/dashboard/main/lease-listing-offer',
  agentIntake: (formId: string) => prefix + `/agent/dashboard/intake/${formId}`,
  agentDashboardNewEBRA: () =>
    prefix + '/agent/dashboard/main/new-ebra-transaction',
  agentDashboardArchivedTransactions: () =>
    prefix + '/agent/dashboard/main/archived-transactions',
  agentDashboardProfile: () => prefix + '/agent/dashboard/profile',
  // agentDashboardHome: () => prefix + '/agent/dashboard/home',
  agentDashboardTransaction: (
    transactionID: string,
    {
      tab = TransactionDetailsTab.Workspace,
      subTab,
      triggerUpload = false,
      documentID = null,
      archived = false,
      viewOnly = false,
      transactionArchived = false,
      returnToDocumentId,
      returnToWorkspaceList,
      returnToWorkspaceSubTab,
      page,
    }: {
      tab?: TransactionDetailsTab;
      subTab?: WorkspaceTabKeys;
      triggerUpload?: boolean;
      documentID?: string | null;
      archived?: boolean;
      viewOnly?: boolean;
      transactionArchived?: boolean;
      returnToDocumentId?: string;
      returnToWorkspaceList?: boolean;
      returnToWorkspaceSubTab?: string;
      page?: number;
    } = {}
  ) => {
    let baseUrl = `/agent/dashboard/transactions/detail?transactionID=${transactionID}`;

    baseUrl += `&tab=${tab}`;
    if (subTab) {
      baseUrl += `&subTab=${subTab}`;
    }

    if (triggerUpload) {
      baseUrl += `&triggerUpload=true`;
    }

    if (documentID) {
      baseUrl += `&documentID=${documentID}`;
    }

    if (archived) {
      baseUrl += `&archived=true`;
    }
    if (viewOnly) {
      baseUrl += `&viewOnly=true`;
    }
    if (transactionArchived) {
      baseUrl += `&transactionArchived=true`;
    }
    if (returnToDocumentId) {
      baseUrl += `&returnToDocumentId=${encodeURIComponent(returnToDocumentId)}`;
    }
    if (returnToWorkspaceList) {
      baseUrl += `&returnToWorkspaceList=true`;
    }
    if (returnToWorkspaceSubTab) {
      baseUrl += `&returnToWorkspaceSubTab=${encodeURIComponent(returnToWorkspaceSubTab)}`;
    }
    if (page) {
      baseUrl += `&page=${page}`;
    }
    return baseUrl;
  },

  // Compliance Dashboard
  complianceDashboardTransactions: () =>
    prefix + '/compliance/dashboard/transactions',
  complianceDashboardEmailTriage: () =>
    prefix + '/compliance/dashboard/email-triage',
  complianceDashboardComplianceTransactions: () =>
    prefix + '/compliance/dashboard/compliance-transactions',
  complianceDashboardChecks: () => prefix + '/compliance/dashboard/checks',
  complianceDashboardSupport: () => prefix + '/compliance/dashboard/support',
  complianceDashboardTasks: () => prefix + '/compliance/dashboard/tasks',
  complianceDashbaordWorkAllocation: () =>
    prefix + '/compliance/dashboard/work-allocation',
  complianceDashboardDailyHubAdmin: () =>
    prefix + '/compliance/dashboard/daily-hub-admin',
  complianceDashboardDailyHub: () => prefix + '/compliance/dashboard/daily-hub',
  complianceDashboardDailyHubTeam: () =>
    prefix + '/compliance/dashboard/daily-hub-team',
  workItemsPreview: () => prefix + `/compliance/work-items-preview`,
  complianceDashboardTransaction: (transactionID: string, subTab?: string) => {
    let url =
      prefix +
      `/compliance/dashboard/transactions/documents?transactionID=${transactionID}`;
    if (subTab) {
      url += `&subTab=${subTab}`;
    }
    return url;
  },
  complianceDashboardTransactionContacts: (transactionID: string) =>
    prefix +
    `/compliance/dashboard/transactions/contacts?transactionID=${transactionID}`,
  complianceDashboardTransactionFacts: (transactionID: string) =>
    prefix +
    `/compliance/dashboard/transactions/facts?transactionID=${transactionID}`,
  complianceDashboardTransactionTasks: (transactionID: string) =>
    prefix +
    `/compliance/dashboard/transactions/tasks?transactionID=${transactionID}`,
  complianceDashboardTransactionVerifyDocument: (
    transactionID: string,
    documentID: string,
    archived: boolean = false
  ) => {
    let url =
      prefix +
      `/compliance/dashboard/transactions/verify?transactionID=${transactionID}&documentID=${documentID}`;
    if (archived) {
      url += `&archived=true`;
    }
    return url;
  },
  complianceDashboardTransactionRequestSignature: (
    transactionID: string,
    {
      returnToDocumentId,
      returnToWorkspaceList,
      returnToWorkspaceSubTab,
    }: {
      returnToDocumentId?: string;
      returnToWorkspaceList?: boolean;
      returnToWorkspaceSubTab?: string;
    } = {}
  ) => {
    let url =
      prefix +
      `/compliance/dashboard/transactions/request-signature?transactionID=${transactionID}`;
    if (returnToDocumentId) {
      url += `&returnToDocumentId=${encodeURIComponent(returnToDocumentId)}`;
    }
    if (returnToWorkspaceList) {
      url += `&returnToWorkspaceList=true`;
    }
    if (returnToWorkspaceSubTab) {
      url += `&returnToWorkspaceSubTab=${encodeURIComponent(returnToWorkspaceSubTab)}`;
    }
    return url;
  },
  complianceDashboardValidation: (checkID: string) =>
    prefix + `/compliance/dashboard/validation?checkID=${checkID}`,
  complianceDashboardTaskTemplates: () =>
    prefix + '/compliance/dashboard/task-templates',
  // Configs (shell-agnostic — reachable from every dashboard's Settings menu)
  configs: () => prefix + '/configs',
  configsEmailConfigIndex: () => prefix + '/configs/email-config',
  configsEmailConfig: (type: string) =>
    prefix + `/configs/email-config/${encodeURIComponent(type)}`,
  configsDocumentRules: (rulesType: RulesType = 'requirementRules') =>
    prefix + `/configs/document-rules/${rulesType}`,
  configsFactConfig: (kind: FactConfigKind = 'facts') =>
    prefix + `/configs/fact-config/${kind}`,
  configsFactConfigVersions: (kind: FactConfigKind) =>
    prefix + `/configs/fact-config/${kind}/versions`,
  configsDocumentTypeLists: () => prefix + '/configs/document-type-lists',
  configsBrokerageSettings: () => prefix + '/configs/brokerage-settings',
  configsCoordinatorMapping: () => prefix + '/configs/coordinator-mapping',
  configsReportingConfig: () => prefix + '/configs/reporting-config',
  configsReportingConfigEditor: (type: string) =>
    prefix + `/configs/reporting-config/${encodeURIComponent(type)}/editor`,
  configsReportingConfigVersions: (type: string) =>
    prefix + `/configs/reporting-config/${encodeURIComponent(type)}/versions`,
  complianceReporting: () => prefix + '/compliance/dashboard/reporting',
  complianceReports: () => prefix + '/compliance/dashboard/reports',
  complianceDashboardTaskForm: (templateId: string) =>
    prefix +
    `/compliance/dashboard/task-templates/task-form?templateId=${templateId}`,
  complianceDashboardTaskFormWithTaskId: (
    templateId: string,
    taskId: string,
    isDuplicate?: boolean
  ) =>
    buildTaskFormUrl(
      prefix,
      '/compliance/dashboard/task-templates/task-form',
      templateId,
      taskId,
      isDuplicate
    ),
  complianceDashboardPaymentValidation: (paymentID: string) =>
    prefix + `/compliance/dashboard/payment-validation?paymentID=${paymentID}`,
  complianceDashboardEarnestly: () =>
    prefix + '/compliance/dashboard/wire-payments',
  complianceDashboardPartnerLoops: () =>
    prefix + '/compliance/dashboard/partner-loops',
  complianceDashboardEFTs: () => prefix + '/compliance/dashboard/efts',
  complianceDashboardRoster: () => prefix + '/compliance/dashboard/roster',
  complianceDashboardRemarksInbox: () =>
    prefix + '/compliance/dashboard/remarks-inbox',
  complianceDashboardTransactionActivityLog: (transactionID: string) =>
    prefix +
    `/compliance/dashboard/transactions/activity-log?transactionID=${transactionID}`,
  complianceDashboardTransactionEmailActivity: (transactionID: string) =>
    prefix +
    `/compliance/dashboard/transactions/email-activity?transactionID=${transactionID}`,
  // Office Admin
  officeAdminChecks: () => prefix + '/office-admin/dashboard/checks',
  officeAdminRoster: () => prefix + '/office-admin/dashboard/roster',
  officeAdminDocuments: (transactionID: string) =>
    prefix + `/office-admin/dashboard/documents?transactionID=${transactionID}`,
  officeAdminDashboardValidation: (checkID: string) =>
    prefix + `/office-admin/dashboard/validation?checkID=${checkID}`,

  //common
  checksRedirect: (checkID: string) =>
    prefix + `/redirect/checks?checkID=${checkID}`,
  transactionRedirect: (transactionID: string) =>
    prefix + `/redirect/transaction?transactionID=${transactionID}`,

  // Agent Upload (public, no auth — uploadKey is an encrypted token)
  agentUpload: (uploadKey: string) => prefix + `/agent-upload/${uploadKey}`,

  // Shared
  privacyPolicy: () => prefix + '/privacy-policy',
  termsAndConditions: () => prefix + '/terms-and-conditions',

  // Form Confirmation
  formConfirmation: (formId: string) =>
    prefix + `/confirmation?formId=${formId}`,

  // Signature
  signature: (slug: string) => prefix + `/signature/${slug}`,
};
